import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Radio, Lock } from 'lucide-react';
import { useAuthStore, SUPERADMIN_TENANT_ID } from '../stores/authStore';
import { simApi, type SimState } from '../services/api';

// Global live-stream clock readout + control (Q2+365 sim). TMEIC-only.
// Reads /sim/state every 5s; play/pause + speed cycle via /sim/control.
const SPEEDS = [1, 5, 15, 60, 300];

export function SimClockChip() {
  const { user } = useAuthStore();
  const [st, setSt] = useState<SimState | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const isTmeic = user && (user.tenant_id === 'tmeic_rambler' || user.tenant_id === SUPERADMIN_TENANT_ID);

  useEffect(() => {
    if (!isTmeic) return;
    let cancelled = false;
    const tick = () => simApi.state().then(s => { if (!cancelled) setSt(s); }).catch(() => {});
    tick();
    timer.current = setInterval(tick, 5000);
    return () => { cancelled = true; if (timer.current) clearInterval(timer.current); };
  }, [isTmeic]);

  if (!isTmeic || !st) return null;

  // Shown in Central Time (Rambler is in West Texas — America/Chicago, CST/CDT
  // handled automatically), labelled "CT" so the readout is unambiguous.
  // display_now re-anchors the hold-out timeline to start "today".
  // Snap the readout to 15-min steps so the clock doesn't visibly "race" at high replay
  // speed: at 60× a smooth clock jumps ~5 min on every 5 s refresh, which reads as a
  // glitch. The underlying data still advances continuously — only the chip presentation
  // is calmed, stepping 13:00 → 13:15 → 13:30 in line with the data cadence.
  const raw = new Date(st.display_now || st.sim_now);
  const d = new Date(raw);
  d.setMinutes(Math.floor(raw.getMinutes() / 15) * 15, 0, 0);
  const fmt = d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago', hour12: false });

  const control = async (body: Parameters<typeof simApi.control>[0]) => {
    setBusy(true);
    try { setSt(await simApi.control(body)); } catch { /* role/404 */ } finally { setBusy(false); }
  };
  const cycleSpeed = () => {
    const i = SPEEDS.indexOf(st.speed);
    control({ action: 'set_speed', speed: SPEEDS[(i + 1) % SPEEDS.length] });
  };

  // SimLock: the clock is frozen at a fixed plant-local instant (e.g. 2026-04-15 20:00 CT).
  // Show a lock (not the pulsing live radio) and hide the play/pause/speed controls — they are
  // no-ops while locked. The time readout stays in Central Time, now showing the frozen instant.
  if (st.locked) {
    return (
      <div
        className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs"
        title={`Demo clock FROZEN at ${st.sim_now_local || st.sim_now} (plant-local Central Time) · real SCADA snapshot · time-travel a single inverter from its detail view`}
      >
        <Lock className="w-3.5 h-3.5 text-amber-400" />
        <span className="font-semibold text-amber-300">SIM</span>
        <span className="font-mono text-amber-100">{fmt} <span className="text-amber-400/70">CT</span></span>
        <span className="text-amber-400/70 text-[10px]">Rambler · frozen</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs"
      title={`Accelerated live replay (×${st.speed}) of real SCADA · ${st.dataset_split} · ${st.progress_pct}% · clock snapped to 15-min so it doesn't race`}
    >
      <Radio className={`w-3.5 h-3.5 ${st.paused ? 'text-dark-500' : 'text-amber-400 animate-pulse'}`} />
      <span className="font-semibold text-amber-300">SIM</span>
      <span className="font-mono text-amber-100">{fmt} <span className="text-amber-400/70">CT</span></span>
      <span className="text-amber-400/70 text-[10px]">Rambler · replay</span>
      <button onClick={cycleSpeed} disabled={busy}
        className="font-mono text-amber-300 hover:text-amber-100 px-1 rounded hover:bg-amber-500/20"
        title="Replay speed — click to change">{st.speed}×</button>
      <button onClick={() => control({ action: st.paused ? 'play' : 'pause' })} disabled={busy}
        className="text-amber-300 hover:text-amber-100" title={st.paused ? 'Play' : 'Pause'}>
        {st.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export default SimClockChip;
